import React from 'react';
import { Link } from 'react-router-dom';
import {
  HomeIcon,
  HeartIcon,
  SparklesIcon,
  MapPinIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';

interface CategoryCard {
  title: string;
  shortTitle: string;
  description: string;
  path: string;
  image: string;
  icon: React.ComponentType<any>;
  badge?: string;
  location: string;
}

const categories: CategoryCard[] = [
  {
    title: 'Affordable Rentals in Dublin',
    shortTitle: 'Affordable Dublin',
    description: 'Under €850/month',
    path: '/affordable-rentals-dublin',
    image:
      'https://images.unsplash.com/photo-1609342122563-a43ac8917a3a?w=400&h=300&fit=crop',
    icon: HomeIcon,
    badge: 'Popular',
    location: 'Dublin'
  },
  {
    title: 'Family Homes in Limerick',
    shortTitle: 'Family Limerick',
    description: 'Spacious houses',
    path: '/family-homes-limerick',
    image:
      'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=400&h=300&fit=crop',
    icon: HeartIcon,
    location: 'Limerick'
  },
  {
    title: 'Energy-Efficient Homes in Dublin',
    shortTitle: 'Eco Dublin',
    description: 'A-rated BER',
    path: '/energy-efficient-homes-dublin',
    image:
      'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop',
    icon: SparklesIcon,
    location: 'Dublin'
  },
  {
    title: 'Affordable House Shares in Cork',
    shortTitle: 'House Shares Cork',
    description: 'Under €600/month',
    path: '/affordable-house-shares-cork',
    image:
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&h=300&fit=crop',
    icon: HomeIcon,
    location: 'Cork'
  }
];

const PropertyCategoriesCarousel: React.FC = () => {
  const analytics = useAnalyticsWithConsent();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = React.useState(false);
  const [showRightArrow, setShowRightArrow] = React.useState(true);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const handleCategoryClick = (category: CategoryCard) => {
    analytics.logEvent('search', {
      search_term: category.title,
      category_path: category.path,
      category_location: category.location,
      has_badge: !!category.badge
    });
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);

      // Calculate which card is currently in view
      const cardWidth = 320; // Approximate card width (w-64 sm:w-72 md:w-80)
      const gap = 24; // Approximate gap (gap-4 sm:gap-5 md:gap-6)
      const index = Math.round(scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(index, categories.length - 1));
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300; // Scroll by approximately one card width
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300; // Scroll by approximately one card width
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="w-full py-6 sm:py-8 md:py-10 px-4 sm:px-6 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl my-6 sm:my-8">
      {/* Header */}
      <div className="mb-5 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-indigo-900 mb-2">
          🏠 Explore Popular Categories
        </h2>
        <p className="text-sm sm:text-base text-gray-600">
          Find your perfect home from our curated selections
        </p>
      </div>

      {/* Horizontal Scroll Container */}
      <div className="relative group/container">
        <div
          ref={scrollContainerRef}
          className="flex gap-4 sm:gap-5 md:gap-6 overflow-x-auto pb-4 pr-20 scrollbar-hide snap-x snap-mandatory scroll-smooth"
        >
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <Link
                key={category.path}
                to={category.path}
                onClick={() => handleCategoryClick(category)}
                className="flex-shrink-0 w-64 sm:w-72 md:w-80 group relative bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 no-underline snap-start"
              >
                {/* Image */}
                <div className="relative h-32 sm:h-36 md:h-40 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                  {/* Badge */}
                  {category.badge && (
                    <div className="absolute top-2 right-2 bg-indigo-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                      {category.badge}
                    </div>
                  )}

                  {/* Location */}
                  <div className="absolute bottom-2 left-2 flex items-center text-white">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    <span className="text-xs font-medium">
                      {category.location}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start mb-2">
                    <Icon className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2">
                        {category.shortTitle}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        {category.description}
                      </p>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-3 flex items-center text-indigo-600 font-medium text-xs sm:text-sm">
                    <span>View Properties</span>
                    <ArrowRightIcon className="ml-1 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Left Scroll Button - Positioned as Overlay */}
        {showLeftArrow && (
          <button
            onClick={handleScrollLeft}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center transform hover:scale-110 opacity-90 hover:opacity-100"
            aria-label="Scroll back to previous categories"
          >
            <ArrowRightIcon className="h-5 w-5 sm:h-6 sm:w-6 rotate-180 group-hover:-translate-x-1 transition-transform" />
          </button>
        )}

        {/* Right Scroll Button - Positioned as Overlay */}
        {showRightArrow && (
          <button
            onClick={handleScrollRight}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 sm:w-14 sm:h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center transform hover:scale-110 opacity-90 hover:opacity-100"
            aria-label="Scroll to see more categories"
          >
            <ArrowRightIcon className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
          </button>
        )}

        {/* Scroll Indicator */}
        <div className="mt-4 flex justify-center gap-2">
          {categories.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 w-8 sm:w-10 rounded-full transition-all duration-300 ${
                index === activeIndex
                  ? 'bg-indigo-600 w-10 sm:w-14'
                  : 'bg-indigo-200'
              }`}
            ></div>
          ))}
        </div>
      </div>

      {/* View All Link */}
      <div className="mt-6 text-center">
        <Link
          to="/explore-categories"
          className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-semibold text-sm sm:text-base no-underline"
        >
          <span>View All Categories</span>
          <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
        </Link>
      </div>

      {/* Custom scrollbar hide styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default PropertyCategoriesCarousel;
